/* u-singles-nav — behaviour for snippets/tcg-singles-nav.liquid.
 * <u-singles-search>:  live results while typing (sections/tcg-singles-live.liquid); Enter opens the full /search page;
 *                      the game scope is the native filter.p.product_type field in the form.
 * <u-singles-sets>:    live filter over all set chips (also searches the hidden ones) + "All sets" toggle.
 * <u-singles-filters>: compact dropdown groups (<details>): one open at a time, outside click / Escape close.
 * Sorting uses a normal GET form with an Apply button (no navigation on change: WCAG 3.2.2).
 */
(function () {
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* Live search: from 2 characters, results for this game's singles render inside the page (Section Rendering API,
     sections/tcg-singles-live.liquid). Enter / the button still open the full, crawlable /search page. Requests are
     debounced, the previous one is aborted, and nothing is fetched until the shopper types. */
  define('u-singles-search', class extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      if (!form || form.dataset.bound) return;
      form.dataset.bound = '1';
      const input = form.querySelector('input[name="q"]');
      const panel = this.querySelector('[data-sl-panel]');
      const clear = this.querySelector('[data-sl-clear]');
      const nav = this.closest('.u-singles-nav');
      let timer = 0;
      let ctrl = null;
      let last = '';

      const typeField = form.querySelector('input[name="filter.p.product_type"]');
      const gameType = typeField ? typeField.value : '';
      // Set pages scope further: the set name joins the query and cards must mention it (data-extra-q / data-scope-text).
      const extraQ = this.dataset.extraQ || '';
      const scopeText = (this.dataset.scopeText || '').toLowerCase();
      const msg = (key, vars) => (this.dataset[key] || '').replace(/%(\w+)%/g, (_, k) => (vars[k] != null ? vars[k] : ''));
      const esc = (t) => String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const reset = () => {
        if (ctrl) ctrl.abort();
        last = '';
        if (panel) { panel.innerHTML = ''; panel.hidden = true; }
        if (nav) nav.classList.remove('is-searching');
        if (clear) clear.hidden = true;
      };
      const run = (term) => {
        if (!panel || term === last) return;
        last = term;
        if (ctrl) ctrl.abort();
        ctrl = new AbortController();
        // No native type filter here (it depends on the per-language search index); cards are scoped below.
        // Search the typed name only: adding the type or set name ("charizard Pokémon Single") widened the match
        // (Shopify ORs the words) and buried the real cards: 2 of 8 Charizard singles vs 8 of 8 with the name alone.
        // The section renders only singles among the first 250 results; game / set scoping happens below.
        const query = new URLSearchParams({ q: term, type: 'product', 'options[prefix]': 'last' });
        const allUrl = form.action + '?' + new URLSearchParams({ q: term, type: 'product' }).toString();
        panel.hidden = false;
        panel.setAttribute('aria-busy', 'true');
        nav && nav.classList.add('is-searching');
        fetch(form.action + '?' + query.toString() + '&section_id=tcg-singles-live', { signal: ctrl.signal })
          .then((r) => (r.ok ? r.text() : ''))
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            // Keep the game's singles (type), of this set when scoped, whose text contains every typed word: the joined
            // query ("jinx Vendetta Riftbound Single") also returns cards that only match the set or the type.
            const words = term.toLowerCase().split(/\s+/).filter(Boolean);
            const cells = [...doc.querySelectorAll('.u-sl__cell')].filter((c) => {
              if (gameType && c.dataset.type !== gameType) return false;
              const text = c.textContent.toLowerCase();
              if (scopeText && text.indexOf(scopeText) === -1) return false;
              return words.every((w) => text.indexOf(w) !== -1);
            });
            let out = '';
            if (cells.length) {
              const shown = cells.slice(0, 12);
              out = '<p class="u-sl__count" role="status">' + esc(msg('i18nCount', { n: cells.length })) + '</p>'
                + '<div class="u-sl__grid">' + shown.map((c) => c.outerHTML).join('') + '</div>'
                + '<a class="u-sl__all" href="' + esc(allUrl) + '">' + esc(msg('i18nAll', { q: term })) + ' <span aria-hidden="true">→</span></a>';
            } else {
              out = '<p class="u-sl__empty" role="status">' + esc(msg('i18nEmpty', { q: term })) + '</p>';
            }
            panel.innerHTML = '<div class="u-sl">' + out + '</div>';
            panel.removeAttribute('aria-busy');
          })
          .catch((e) => { if (e.name !== 'AbortError') panel.removeAttribute('aria-busy'); });
      };

      input.addEventListener('input', () => {
        const term = (input.value || '').trim();
        if (clear) clear.hidden = !input.value;
        clearTimeout(timer);
        if (term.length < 2) { reset(); return; }
        timer = setTimeout(() => run(term), 280);
      });
      input.addEventListener('keydown', (e) => { if (e.key === 'Escape') { input.value = ''; reset(); } });
      if (clear) clear.addEventListener('click', () => { input.value = ''; reset(); input.focus(); });
      // With JS the search stays on the page (results above the filters); without JS the form opens /search.
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const term = (input.value || '').trim();
        if (!term) { input.focus(); return; }
        clearTimeout(timer);
        last = '';
        run(term);
      });
    }
  });

  define('u-singles-sets', class extends HTMLElement {
    connectedCallback() {
      const group = this.closest('.u-singles-nav__group');
      const toggle = this.querySelector('.u-singles-nav__toggle');
      const filter = this.querySelector('.u-singles-nav__filter');
      if (!group || !toggle || toggle.dataset.bound) return;
      toggle.dataset.bound = '1';
      const items = () => group.querySelectorAll('.u-singles-nav__chips li');
      let open = false;
      const render = () => {
        const q = filter.value.trim().toLowerCase();
        group.classList.toggle('is-expanded', open || !!q);
        items().forEach((li) => {
          const extra = li.classList.contains('u-singles-nav__more');
          const match = !q || li.textContent.toLowerCase().indexOf(q) !== -1;
          li.hidden = q ? !match : (extra && !open);
        });
      };
      toggle.addEventListener('click', () => {
        open = !open;
        toggle.setAttribute('aria-expanded', String(open));
        toggle.textContent = open ? toggle.dataset.labelLess : toggle.dataset.labelMore;
        render();
      });
      filter.addEventListener('input', render);
    }
  });

  /* Compact filter dropdowns: one open at a time; outside click or Escape closes them. */
  define('u-singles-filters', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound) return;
      this.dataset.bound = '1';
      const groups = () => [...this.querySelectorAll('details.u-singles-nav__group')];
      this.addEventListener('toggle', (e) => {
        if (e.target.open) groups().forEach((d) => { if (d !== e.target) d.open = false; });
      }, true);
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) groups().forEach((d) => { d.open = false; }); });
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const open = groups().find((d) => d.open);
        if (open) { open.open = false; open.querySelector('summary').focus(); }
      });
    }
  });
})();
